/* eslint-disable camelcase */
import { Grid, Switch } from '@mui/material';
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalDialog, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import RoutalIcon from '../../../../assets/svg/RoutalIcon';
import { Project } from '../../../../domain/project/Project';
import { ProjectCustomerSMSTypes } from '../../../../domain/project/ProjectDTO';
import { ProjectCustomerSMSItem } from '../../../../domain/project/projectCustomerSMS/ProjectCustomerSMSItem';
import { Subscription } from '../../../../domain/subscription/Subscription';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useUpdateProjectCommunicationSMS } from '../../../../hooks/project/useUpdateProjectCommunicationSMS';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import EditableContentSMS from './EditableContentSMS';
import {
  NotificationsSMSContainer,
  NotificationsSMSContainerHeader,
  NotificationsSMSMobileContainer,
  NotificationsSMSMobileContainerContent,
  NotificationsSMSMobileContainerContentBody,
  NotificationsSMSMobileSpeechBubble,
} from './NotificationsSMS.styled';
import { NotificationsSMSEnableDialog } from './NotificationsSMSEnableDialog';

const FAKE_MONGO_ID = `123456789012345678901234`;

const MAX_SMS = 8;

const CHARS_PER_SMS = 153;

const CUSTOM_FIELD_PREFIX = `custom_fields.`;

const customerSMSSubstitutions = [
  `label`,
  `external_id`,
  `location_details`,
  `comments`,
  `email`,
  `reference_person`,
  `phone`,
  `url`,
  `pin`,
];

type NotificationsSMSProps = {
  smsType: ProjectCustomerSMSTypes;
  project: Project;
  organizationSubscription?: Subscription;
};

export const NotificationsSMSTemplate = ({ smsType, project, organizationSubscription }: NotificationsSMSProps) => {
  const [isEnableSMSDialogOpen, setIsEnableSMSDialogOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<ProjectCustomerSMSItem | undefined>(undefined);
  const [smsTemplate, setSMSTemplate] = useState<ProjectCustomerSMSItem | undefined>();
  const [visibleSubstitutions, setVisibleSubstitutions] = useState<boolean>(false);
  const { run: editCommunicationSMS } = useUpdateProjectCommunicationSMS({ project });

  useEffect(() => {
    // Only showing the pickup sms if pickups are available in project view constraints.
    if (!project.customer_sms) return;

    const smsData = project.customer_sms[smsType];

    if (!smsData) return;

    const body = smsData.body ?? ``;

    setSMSTemplate(
      ProjectCustomerSMSItem.create({
        from: smsData.from,
        enabled: smsData.enabled,
        updated_at: smsData.updated_at,
        ...(body ? { body } : {}),
        include_customers_url: smsData.include_customers_url ?? false,
      })
    );
  }, [smsType, project]);

  const getCustomersExampleLink = () => `${project.getCustomersDomain()}/example`;

  const getFakeCustomersLink = () => `${project.getCustomersDomain()}/stop/${FAKE_MONGO_ID}`;

  const updateSMSItem = async (itemToUpdate?: ProjectCustomerSMSItem) => {
    if (!itemToUpdate) return;

    const body = itemToUpdate.body ?? ``;
    editCommunicationSMS({
      smsType,
      smsData: {
        ...(body ? { body } : {}),
        enabled: itemToUpdate.enabled,
        include_customers_url: itemToUpdate.include_customers_url,
      },
    });
  };

  const isSaveButtonDisabled = (projectCustomerSMSType: ProjectCustomerSMSItem) => {
    if (!project.customer_sms) return true;

    const sms = project.customer_sms[smsType];

    if (!sms) return true;

    const smsBody = sms.body ?? ``;
    const projectCustomerSMSTypeBody = projectCustomerSMSType.body ?? ``;

    return (
      smsBody === projectCustomerSMSTypeBody &&
      (sms.include_customers_url ?? false) === projectCustomerSMSType.include_customers_url
    );
  };

  if (!smsTemplate) return null;

  const getCustomersLinkSize = () => {
    if (!smsTemplate.include_customers_url) {
      return 0;
    }

    return getFakeCustomersLink().length;
  };

  const getBodyLength = () => {
    if (!smsTemplate?.body || smsTemplate.body === ``) {
      return intl.formatMessage({
        id: `projects.communication.messages.emails.${project.getTranslationsSMSType(smsType)}.placeholder`,
      }).length;
    }
    return smsTemplate.body.length + getCustomersLinkSize();
  };

  const countSMSUsed = () => Math.ceil(getBodyLength() / CHARS_PER_SMS);

  const isTemplateEnabled = () => {
    return organizationSubscription?.sms.enabled && smsTemplate.enabled;
  };

  return (
    <>
      <NotificationsSMSContainer>
        <NotificationsSMSContainerHeader>
          <Grid item>
            <Text
              variant="body-medium"
              intlId={`projects.communication.messages.sms.${project.getTranslationsSMSType(smsType)}`}
            />
          </Grid>

          <Grid item>
            <Grid container direction="row" alignItems="center" justifyContent="flex-end" wrap="nowrap">
              <Grid item container direction="row" alignItems="center" style={{ width: `unset` }} wrap="nowrap">
                <Text variant="label" intlId="projects.communication.messages.sms.include_link" />
                <Switch
                  checked={smsTemplate.include_customers_url ?? false}
                  value={smsTemplate.include_customers_url ?? false}
                  onChange={(event: any) => {
                    const { checked } = event.target;
                    smsTemplate.include_customers_url = checked;
                    setSMSTemplate(ProjectCustomerSMSItem.create(smsTemplate.getValue()));
                  }}
                  color="primary"
                />
              </Grid>

              <HSpacer small />

              <Button
                styleType="primary"
                variant="default"
                disabled={isSaveButtonDisabled(smsTemplate)}
                onClick={() => {
                  updateSMSItem(smsTemplate);
                }}
                intlId="save"
              />

              <HSpacer small />

              <Button
                styleType="secondary"
                variant="default"
                style={{
                  backgroundColor: isTemplateEnabled() ? RoutalPalette.danger : RoutalPalette.success,
                }}
                onClick={() => {
                  if (!organizationSubscription?.sms.enabled) {
                    setIsEnableSMSDialogOpen(true);
                  }
                  if (!isTemplateEnabled()) {
                    setSelectedItem(
                      ProjectCustomerSMSItem.create({
                        ...smsTemplate.getValue(),
                        enabled: true,
                      })
                    );
                    setIsEnableSMSDialogOpen(true);
                  } else {
                    updateSMSItem(
                      ProjectCustomerSMSItem.create({
                        ...smsTemplate.getValue(),
                        enabled: false,
                      })
                    );
                  }
                }}
                intlId={`projects.communication.messages.sms.${
                  isTemplateEnabled() ? `disable_button` : `enable_button`
                }`}
              />
            </Grid>
          </Grid>
        </NotificationsSMSContainerHeader>

        <NotificationsSMSMobileContainer
          onClick={() => {
            setVisibleSubstitutions(false);
          }}
        >
          <NotificationsSMSMobileContainerContent>
            <Grid
              container
              direction="column"
              alignContent="center"
              justifyContent="center"
              style={{
                padding: `8px 0`,
                display: `flex`,
                flexDirection: `column`,
                alignItems: `center`,
                gap: `4px`,
                backgroundColor: RoutalPalette.primary60,
                borderRadius: `10px 10px 0 0`,
              }}
            >
              <Grid
                style={{
                  display: `flex`,
                  width: `35px`,
                  height: `35px`,
                  padding: `10px`,
                  justifyContent: `center`,
                  alignItems: `center`,
                  gap: `10px`,
                  borderRadius: `50px`,
                  backgroundColor: RoutalPalette.primary20,
                }}
              >
                <RoutalIcon />
              </Grid>
              <Text variant="ultra-small" style={{ textAlign: `center`, color: RoutalPalette.primary20n }}>
                Routal
              </Text>
            </Grid>

            <Grid container justifyContent="center" style={{ position: `relative`, height: `100%` }}>
              <Grid
                style={{
                  position: `absolute`,
                  width: `100%`,
                  height: `100%`,
                  top: 0,
                  left: 0,
                  backgroundColor: RoutalPalette.primary20n,
                  borderRadius: `0 0 10px 10px`,
                }}
              />

              <NotificationsSMSMobileContainerContentBody
                onMouseLeave={() => {
                  setVisibleSubstitutions(false);
                }}
              >
                <NotificationsSMSMobileSpeechBubble
                  item
                  onMouseEnter={() => {
                    setVisibleSubstitutions(true);
                  }}
                >
                  <EditableContentSMS
                    value={smsTemplate.body ?? ``}
                    // Maximum characters (MAX_SMS SMS with CHARS_PER_SMS characters each)
                    limit={MAX_SMS * CHARS_PER_SMS - getCustomersLinkSize()}
                    inputStyle={{
                      fontFamily: `Geist Variable`,
                      fontSize: `14px`,
                      outline: `unset`,
                      padding: `18.5px 14px`,
                      border: `none`,
                      backgroundColor: `transparent`,
                      paddingBottom: 0,
                      overflowWrap: `anywhere`,
                    }}
                    placeholder={intl.formatMessage({
                      id: `projects.communication.messages.sms.${project.getTranslationsSMSType(smsType)}.placeholder`,
                    })}
                    onValueChange={(value) => {
                      smsTemplate.body = value !== `` ? value : undefined;
                      setSMSTemplate(ProjectCustomerSMSItem.create(smsTemplate.getValue()));
                    }}
                    visibleSubstitutions={visibleSubstitutions}
                    setVisibleSubstitutions={setVisibleSubstitutions}
                    substitutions={[
                      ...customerSMSSubstitutions.map((substitution) => ({
                        id: substitution,
                        label: intl.formatMessage({
                          id: `projects.communication.messages.sms.substitutions.${substitution}`,
                        }),
                      })),
                      ...(project?.custom_fields?.client
                        ? project.custom_fields.client.map((customField: CustomFieldDefinition) => ({
                            id: `${CUSTOM_FIELD_PREFIX}${customField.custom_id}`,
                            label: customField.label,
                          }))
                        : []),
                    ]}
                    customersLink={
                      smsTemplate.include_customers_url ? (
                        <Text
                          style={{
                            wordBreak: `break-all`,
                            width: `calc(100% - 30px)`,
                            padding: `0px 14px`,
                            paddingBottom: `8px`,
                            fontSize: `14px`,
                          }}
                          variant="button"
                          onClick={() => {
                            window.open(`${getCustomersExampleLink()}`, `_blank`);
                          }}
                        >
                          {getFakeCustomersLink()}
                        </Text>
                      ) : undefined
                    }
                  />
                </NotificationsSMSMobileSpeechBubble>
              </NotificationsSMSMobileContainerContentBody>
            </Grid>
          </NotificationsSMSMobileContainerContent>

          <Grid
            container
            direction="row"
            alignContent="center"
            justifyContent="space-between"
            style={{ width: `328px`, paddingBottom: `20px` }}
          >
            <Grid item>
              <Text
                style={{ fontSize: `12px` }}
                intlId="projects.communication.messages.sms.chars_used"
                intlValues={{ used: getBodyLength() }}
              />
            </Grid>

            <Grid item>
              <Text
                variant="label"
                intlId="projects.communication.messages.sms.sms_used"
                intlValues={{
                  remaining:
                    getBodyLength() > 0 ? CHARS_PER_SMS - (CHARS_PER_SMS * countSMSUsed() - getBodyLength()) : 0,
                  smsUsed: countSMSUsed(),
                }}
              />
            </Grid>
          </Grid>
        </NotificationsSMSMobileContainer>
      </NotificationsSMSContainer>

      {isEnableSMSDialogOpen ? (
        <>
          {organizationSubscription?.sms.enabled ? (
            <RoutalDialog
              open={!!isEnableSMSDialogOpen}
              dialogProps={{
                fullWidth: true,
                maxWidth: `sm`,
              }}
              onCloseDialog={() => {
                setIsEnableSMSDialogOpen(false);
                setSelectedItem(undefined);
              }}
              titleContent={<Text variant="h6" intlId="projects.communication.messages.sms.enable.title" />}
              footerContent={
                <Grid container justifyContent="flex-end">
                  <Button
                    variant="flat"
                    onClick={() => {
                      setIsEnableSMSDialogOpen(false);
                      setSelectedItem(undefined);
                    }}
                    intlId="cancel"
                  />

                  <HSpacer small />

                  <Button
                    styleType="primary"
                    onClick={async () => {
                      await updateSMSItem(selectedItem);
                      setSelectedItem(undefined);
                      setIsEnableSMSDialogOpen(false);
                    }}
                    intlId="confirm"
                  />
                </Grid>
              }
            >
              <Grid container direction="column">
                <Text intlId="projects.communication.messages.sms.enable.description" />
              </Grid>
            </RoutalDialog>
          ) : (
            <NotificationsSMSEnableDialog
              open
              onClose={() => {
                setIsEnableSMSDialogOpen(false);
                setSelectedItem(undefined);
              }}
            />
          )}
        </>
      ) : null}
    </>
  );
};
